import React, { useEffect, useState, useCallback } from 'react';
import { BRAND, poweredByUrl } from '../../../data/brand';
import { useNavigate } from 'react-router-dom';
import ImageViewer from 'react-simple-image-viewer';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import { useUser } from '../../../contexts/UserContext';
import colordata from '../../../data/colors.json';
import {
    getReadableTextColor,
    normalizeColor,
} from '../../../utils/colorContrast';

const daysOfWeek = [
    'sunday',
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
];

function capitalizeFirstLetter(string) {
    return string.charAt(0).toUpperCase() + string.slice(1);
}

function convertTo12HourFormat(time24) {
    const [hours, minutes] = time24.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const hours12 = hours % 12 || 12;
    return `${hours12.toString().padStart(2, '0')}:${minutes
        .toString()
        .padStart(2, '0')} ${period}`;
}

function hexToRgb(hex) {
    let cleaned = normalizeColor(hex).replace('#', '');
    if (cleaned.length === 3) {
        cleaned = cleaned
            .split('')
            .map((char) => char + char)
            .join('');
    }
    const bigint = parseInt(cleaned, 16);
    return {
        r: (bigint >> 16) & 255,
        g: (bigint >> 8) & 255,
        b: bigint & 255,
    };
}

function darkenHex(hex, amount) {
    const { r, g, b } = hexToRgb(hex);
    const dr = Math.max(0, Math.round(r * (1 - amount)));
    const dg = Math.max(0, Math.round(g * (1 - amount)));
    const db = Math.max(0, Math.round(b * (1 - amount)));
    return `#${dr.toString(16).padStart(2, '0')}${dg
        .toString(16)
        .padStart(2, '0')}${db.toString(16).padStart(2, '0')}`;
}

function buildColorScheme(primaryColor) {
    const primary = normalizeColor(primaryColor) || '#2563eb';
    const primaryDark = darkenHex(primary, 0.15);
    const { r, g, b } = hexToRgb(primary);
    return {
        primary,
        primaryText: getReadableTextColor(primary),
        primaryDark,
        primaryDarkText: getReadableTextColor(primaryDark),
        primaryLight: `rgba(${r}, ${g}, ${b}, 0.1)`,
        primaryLighter: `rgba(${r}, ${g}, ${b}, 0.05)`,
        gradient: `linear-gradient(135deg, ${primary} 0%, ${primaryDark} 100%)`,
    };
}

const findColorForKey = (key, position) => {
    const item = colordata.find((obj) => obj[key] !== undefined);
    return item ? item[key][position] : '';
};
const getPortalThemeColor = (portal, key, fallback) => {
    return portal && portal.theme_colors && portal.theme_colors[key]
        ? portal.theme_colors[key]
        : fallback;
};

function formatAddress(value) {
    if (!value || typeof value === 'string') return value || '';
    const { street, city, state, zip_code } = value;
    const lineOne = [street, city].filter(Boolean).join(', ');
    const lineTwo = [state, zip_code].filter(Boolean).join(' - ');
    return [lineOne, lineTwo].filter(Boolean).join('\n');
}

function formatAddressOneLine(value) {
    if (!value) return '';
    if (typeof value === 'string') return value;
    const { street, city, state, zip_code } = value;
    return [street, city, state, zip_code].filter(Boolean).join(', ');
}

function checkIsOpenNow(businessHours) {
    if (!businessHours || businessHours.length === 0) return null;
    const now = new Date();
    const todayName = daysOfWeek[now.getDay()];
    const todayEntry = businessHours.find((h) => h.day === todayName);
    if (!todayEntry || !todayEntry.intervals || todayEntry.intervals.length === 0) {
        return false;
    }
    const currentMinutes = now.getHours() * 60 + now.getMinutes();
    return todayEntry.intervals.some((iv) => {
        const [fromH, fromM] = iv.from.split(':').map(Number);
        const [toH, toM] = iv.to.split(':').map(Number);
        return currentMinutes >= fromH * 60 + fromM && currentMinutes < toH * 60 + toM;
    });
}

function getSubdomain() {
    if (
        process.env.REACT_APP_TEST_ENV &&
        process.env.REACT_APP_TEST_ENV_SUBDOMAIN
    ) {
        return process.env.REACT_APP_TEST_ENV_SUBDOMAIN;
    }
    const fullHostname = window.location.hostname;
    return fullHostname.split('.')[0];
}

const ModernHomepage = () => {
    const { user } = useUser();
    const navigate = useNavigate();
    const subdomain = getSubdomain();

    const primaryColor = getPortalThemeColor(
        user && user.portal,
        'website_primary',
        findColorForKey(subdomain, 'primary') || '#2563eb'
    );
    const colors = buildColorScheme(primaryColor);

    const businessName =
        user?.business?.business_name ||
        user?.business?.business_user_name ||
        '';
    const phone = user?.business?.business_phone || '';
    const email = user?.business?.business_email || '';
    const address = formatAddress(user?.business?.business_address);
    const logo = user?.business?.business_logo || '';

    const heroImage = user?.portal?.hero_image || '';
    const aboutUs = user?.portal?.about_us || {};
    const heroSubtext =
        aboutUs.description ||
        'We take pride in providing top-notch solutions to meet your unique needs.';

    const servicesList = (user?.portal?.services_list || []).filter(
        (s) => s && s.title
    );
    const hasServices = servicesList.length > 0;

    const galleryImages = (user?.portal?.gallery_images || []).filter(
        (img) => img && img.image
    );
    const hasGallery = galleryImages.length > 0;

    const businessHours =
        user?.portal?.working_hours?.business_hours || [];
    const hasHours = businessHours.length > 0;
    const sortedHours = [...businessHours].sort(
        (a, b) => daysOfWeek.indexOf(a.day) - daysOfWeek.indexOf(b.day)
    );

    const openNow = hasHours ? checkIsOpenNow(businessHours) : null;

    const { isLoaded } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: 'AIzaSyBZqPyoqaLXZpORlMrdAW1-xUl3PE4AUM4',
    });
    const [cordData, setCordData] = useState(null);
    const [validAddress, setValidAddress] = useState(false);

    const onMapLoad = useCallback(
        (map) => {
            map.setZoom(validAddress ? 15 : 11);
        },
        [validAddress]
    );

    useEffect(() => {
        const addressStr = formatAddressOneLine(user?.business?.business_address);
        if (addressStr) {
            fetch(
                `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
                    addressStr
                )}&key=AIzaSyBZqPyoqaLXZpORlMrdAW1-xUl3PE4AUM4`
            )
                .then((res) => res.json())
                .then((data) => {
                    if (data.status === 'OK') {
                        setCordData(data.results[0].geometry.location);
                        setValidAddress(true);
                    }
                })
                .catch(() => {});
        }
    }, [user]);

    useEffect(() => {
        if (businessName) {
            document.title = businessName;
        }
    }, [businessName]);

    useEffect(() => {
        const link = document.createElement('link');
        link.href =
            'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap';
        link.rel = 'stylesheet';
        document.head.appendChild(link);
        return () => {
            document.head.removeChild(link);
        };
    }, []);

    const [currentImage, setCurrentImage] = useState(0);
    const [isViewerOpen, setIsViewerOpen] = useState(false);

    const imageArray = galleryImages.map((img) => img.image);

    const openImageViewer = useCallback((index) => {
        setCurrentImage(index);
        setIsViewerOpen(true);
    }, []);

    const closeImageViewer = () => {
        setCurrentImage(0);
        setIsViewerOpen(false);
    };

    const goToBook = () => navigate('/book');

    return (
        <div
            style={{
                fontFamily:
                    "'Inter', system-ui, -apple-system, sans-serif",
                color: '#1f2937',
                lineHeight: 1.6,
                WebkitFontSmoothing: 'antialiased',
            }}
        >
            {/* ── Nav ── */}
            <nav
                style={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 100,
                    background: 'rgba(255,255,255,0.97)',
                    backdropFilter: 'blur(8px)',
                    borderBottom: '1px solid #e5e7eb',
                    padding: '0 2rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    height: 64,
                }}
            >
                <a
                    href="/"
                    style={{
                        fontWeight: 800,
                        fontSize: '1.3rem',
                        color: colors.primary,
                        textDecoration: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                    }}
                >
                    {logo && (
                        <img
                            src={logo}
                            alt={businessName}
                            style={{
                                maxHeight: 36,
                                maxWidth: 36,
                                objectFit: 'contain',
                            }}
                        />
                    )}
                    {businessName}
                </a>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '1.5rem',
                    }}
                >
                    {hasServices && (
                        <a
                            href="#services"
                            style={{
                                fontSize: '0.9rem',
                                color: '#6b7280',
                                fontWeight: 500,
                                textDecoration: 'none',
                            }}
                        >
                            Services
                        </a>
                    )}
                    {(hasHours || phone || address) && (
                        <a
                            href="#contact"
                            style={{
                                fontSize: '0.9rem',
                                color: '#6b7280',
                                fontWeight: 500,
                                textDecoration: 'none',
                            }}
                        >
                            Contact
                        </a>
                    )}
                    <button
                        onClick={goToBook}
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            background: colors.primary,
                            color: colors.primaryText,
                            padding: '0.5rem 1.25rem',
                            borderRadius: 10,
                            fontWeight: 600,
                            fontSize: '0.85rem',
                            border: 'none',
                            cursor: 'pointer',
                        }}
                    >
                        Book Now
                    </button>
                </div>
            </nav>

            {/* ── Hero ── */}
            <section
                style={{
                    padding: heroImage ? '3rem 2rem' : '5rem 2rem',
                    background: colors.primaryLighter,
                }}
            >
                <div
                    style={{
                        maxWidth: 1100,
                        margin: '0 auto',
                        display: 'grid',
                        gridTemplateColumns: heroImage
                            ? '1fr 1fr'
                            : '1fr',
                        gap: '2.5rem',
                        alignItems: 'center',
                    }}
                    className="hp2-hero-grid"
                >
                    <div
                        style={{
                            textAlign: heroImage ? 'left' : 'center',
                        }}
                    >
                        <h1
                            style={{
                                fontSize: heroImage
                                    ? '2.5rem'
                                    : '3rem',
                                fontWeight: 900,
                                color: '#111827',
                                marginBottom: '0.75rem',
                                lineHeight: 1.15,
                            }}
                        >
                            {businessName}
                        </h1>
                        <p
                            style={{
                                fontSize: '1.1rem',
                                color: '#6b7280',
                                marginBottom: '1.25rem',
                                lineHeight: 1.6,
                                maxWidth: heroImage ? 'none' : 600,
                                margin: heroImage
                                    ? undefined
                                    : '0 auto',
                                marginBottom: '1.25rem',
                            }}
                        >
                            {heroSubtext}
                        </p>
                        {address && (
                            <p
                                style={{
                                    color: '#6b7280',
                                    marginBottom: '1.5rem',
                                    fontSize: '0.95rem',
                                    whiteSpace: 'pre-line',
                                }}
                            >
                                &#128205; {address}
                            </p>
                        )}
                        <div
                            style={{
                                display: 'flex',
                                gap: '1rem',
                                justifyContent: heroImage
                                    ? 'flex-start'
                                    : 'center',
                                flexWrap: 'wrap',
                            }}
                        >
                            <button
                                onClick={goToBook}
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    background: colors.primary,
                                    color: colors.primaryText,
                                    padding: '0.85rem 2rem',
                                    borderRadius: 10,
                                    fontWeight: 600,
                                    fontSize: '0.95rem',
                                    border: 'none',
                                    cursor: 'pointer',
                                }}
                            >
                                &#128197; Book Now
                            </button>
                            {phone && (
                                <a
                                    href={`tel:${phone}`}
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '0.5rem',
                                        background: '#fff',
                                        color: colors.primary,
                                        padding: '0.85rem 2rem',
                                        borderRadius: 10,
                                        fontWeight: 600,
                                        border: `2px solid ${colors.primary}`,
                                        textDecoration: 'none',
                                        fontSize: '0.95rem',
                                    }}
                                >
                                    &#128222; Call {phone}
                                </a>
                            )}
                        </div>
                    </div>
                    {heroImage && (
                        <div
                            style={{
                                borderRadius: 16,
                                overflow: 'hidden',
                                boxShadow:
                                    '0 20px 40px rgba(0,0,0,0.1)',
                            }}
                        >
                            <img
                                src={heroImage}
                                alt={businessName}
                                style={{
                                    width: '100%',
                                    height: 400,
                                    objectFit: 'cover',
                                    display: 'block',
                                }}
                            />
                        </div>
                    )}
                </div>
            </section>

            {/* ── Photo Gallery ── */}
            {hasGallery && (
                <section
                    style={{
                        padding: '3rem 2rem',
                        maxWidth: 1100,
                        margin: '0 auto',
                    }}
                >
                    <h2
                        style={{
                            textAlign: 'center',
                            fontSize: '1.75rem',
                            fontWeight: 800,
                            marginBottom: '1.5rem',
                            color: '#111827',
                        }}
                    >
                        Gallery
                    </h2>
                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns:
                                'repeat(auto-fill, minmax(220px, 1fr))',
                            gap: '1rem',
                        }}
                    >
                        {galleryImages.map((photo, idx) => (
                            <div
                                key={idx}
                                style={{
                                    borderRadius: 12,
                                    overflow: 'hidden',
                                    aspectRatio: '4/3',
                                    cursor: 'pointer',
                                }}
                                onClick={() => openImageViewer(idx)}
                            >
                                <img
                                    src={photo.image}
                                    alt={`${businessName} gallery ${idx + 1}`}
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                    }}
                                    loading="lazy"
                                />
                            </div>
                        ))}
                    </div>
                    {isViewerOpen && (
                        <ImageViewer
                            src={imageArray}
                            currentIndex={currentImage}
                            disableScroll={false}
                            closeOnClickOutside={true}
                            onClose={closeImageViewer}
                            backgroundStyle={{ zIndex: 1000 }}
                            closeComponent={
                                <span
                                    style={{
                                        color: '#fff',
                                        fontSize: '2.5rem',
                                        fontWeight: 700,
                                        cursor: 'pointer',
                                        lineHeight: 1,
                                        padding: '0.5rem',
                                        userSelect: 'none',
                                    }}
                                >
                                    &times;
                                </span>
                            }
                        />
                    )}
                </section>
            )}

            {/* ── Services ── */}
            {hasServices && (
                <section
                    id="services"
                    style={{
                        padding: '4rem 2rem',
                        maxWidth: 1100,
                        margin: '0 auto',
                    }}
                >
                    <h2
                        style={{
                            textAlign: 'center',
                            fontSize: '1.75rem',
                            fontWeight: 800,
                            marginBottom: '0.5rem',
                            color: '#111827',
                        }}
                    >
                        Our Services
                    </h2>
                    <p
                        style={{
                            textAlign: 'center',
                            color: '#6b7280',
                            marginBottom: '2rem',
                        }}
                    >
                        What we offer
                    </p>
                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns:
                                'repeat(auto-fit, minmax(250px, 1fr))',
                            gap: '1.5rem',
                        }}
                    >
                        {servicesList.map((service, idx) => (
                            <div
                                key={idx}
                                style={{
                                    background: '#fff',
                                    border: '1px solid #e5e7eb',
                                    borderRadius: 12,
                                    padding: '1.5rem',
                                    textAlign: 'center',
                                }}
                            >
                                <div
                                    style={{
                                        width: 48,
                                        height: 48,
                                        background:
                                            colors.primaryLight,
                                        borderRadius: 12,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        margin: '0 auto 1rem',
                                    }}
                                >
                                    <span
                                        style={{
                                            fontSize: '1.3rem',
                                        }}
                                    >
                                        &#9733;
                                    </span>
                                </div>
                                <h3
                                    style={{
                                        fontSize: '1.05rem',
                                        fontWeight: 700,
                                        marginBottom: '0.5rem',
                                        color: '#111827',
                                    }}
                                >
                                    {service.title}
                                </h3>
                                {service.description && (
                                    <p
                                        style={{
                                            color: '#6b7280',
                                            fontSize: '0.9rem',
                                            lineHeight: 1.5,
                                            margin: 0,
                                            wordBreak: 'break-word',
                                            overflowWrap: 'break-word',
                                        }}
                                    >
                                        {service.description}
                                    </p>
                                )}
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {/* ── Contact Section ── */}
            {(phone || email || address || hasHours) && (
                <section
                    id="contact"
                    style={{ padding: '4rem 2rem' }}
                >
                    <div
                        style={{
                            maxWidth: 1100,
                            margin: '0 auto',
                        }}
                    >
                        <h2
                            style={{
                                textAlign: 'center',
                                fontSize: '1.75rem',
                                fontWeight: 800,
                                marginBottom: '0.5rem',
                                color: '#111827',
                            }}
                        >
                            Get In Touch
                        </h2>
                        <p
                            style={{
                                textAlign: 'center',
                                color: '#6b7280',
                                marginBottom: '2rem',
                            }}
                        >
                            We'd love to hear from you
                        </p>
                        <div
                            style={{
                                display: 'grid',
                                gridTemplateColumns: hasHours
                                    ? '1fr 1fr'
                                    : '1fr',
                                gap: '2rem',
                            }}
                            className="hp2-contact-grid"
                        >
                            {/* Hours Table */}
                            {hasHours && (
                                <div
                                    style={{
                                        background: '#fff',
                                        border: '1px solid #e5e7eb',
                                        borderRadius: 16,
                                        padding: '1.5rem',
                                    }}
                                >
                                    <h3
                                        style={{
                                            fontWeight: 700,
                                            marginBottom: '1rem',
                                            fontSize: '1.1rem',
                                            color: '#111827',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.5rem',
                                        }}
                                    >
                                        Business Hours
                                        {openNow !== null && (
                                            <span
                                                style={{
                                                    display: 'inline-block',
                                                    background: openNow
                                                        ? '#dcfce7'
                                                        : '#fee2e2',
                                                    color: openNow
                                                        ? '#16a34a'
                                                        : '#dc2626',
                                                    fontSize: '0.75rem',
                                                    padding:
                                                        '0.2rem 0.6rem',
                                                    borderRadius: 12,
                                                    fontWeight: 600,
                                                }}
                                            >
                                                {openNow
                                                    ? '\u25CF Open Now'
                                                    : 'Closed'}
                                            </span>
                                        )}
                                    </h3>
                                    <table
                                        style={{
                                            width: '100%',
                                            borderCollapse:
                                                'collapse',
                                        }}
                                    >
                                        <tbody>
                                            {sortedHours.map(
                                                (h, idx) => {
                                                    const isClosed =
                                                        !h.intervals ||
                                                        h.intervals
                                                            .length ===
                                                            0;
                                                    const timeStr =
                                                        isClosed
                                                            ? 'Closed'
                                                            : h.intervals
                                                                  .map(
                                                                      (
                                                                          iv
                                                                      ) =>
                                                                          `${convertTo12HourFormat(
                                                                              iv.from
                                                                          )} – ${convertTo12HourFormat(
                                                                              iv.to
                                                                          )}`
                                                                  )
                                                                  .join(
                                                                      ', '
                                                                  );
                                                    return (
                                                        <tr key={idx}>
                                                            <td
                                                                style={{
                                                                    padding:
                                                                        '0.5rem 0',
                                                                    borderBottom:
                                                                        '1px solid #f3f4f6',
                                                                    fontWeight: 600,
                                                                    fontSize:
                                                                        '0.9rem',
                                                                    color: '#374151',
                                                                }}
                                                            >
                                                                {capitalizeFirstLetter(
                                                                    h.day
                                                                )}
                                                            </td>
                                                            <td
                                                                style={{
                                                                    padding:
                                                                        '0.5rem 0',
                                                                    borderBottom:
                                                                        '1px solid #f3f4f6',
                                                                    textAlign:
                                                                        'right',
                                                                    color: isClosed
                                                                        ? '#dc2626'
                                                                        : '#6b7280',
                                                                    fontSize:
                                                                        '0.9rem',
                                                                }}
                                                            >
                                                                {timeStr}
                                                            </td>
                                                        </tr>
                                                    );
                                                }
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {/* Contact Info */}
                            <div
                                style={{
                                    background: '#fff',
                                    border: '1px solid #e5e7eb',
                                    borderRadius: 16,
                                    padding: '1.5rem',
                                }}
                            >
                                <h3
                                    style={{
                                        fontWeight: 700,
                                        marginBottom: '1rem',
                                        fontSize: '1.1rem',
                                        color: '#111827',
                                    }}
                                >
                                    Contact Information
                                </h3>
                                {phone && (
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.75rem',
                                            marginBottom: '1rem',
                                        }}
                                    >
                                        <div
                                            style={{
                                                width: 40,
                                                height: 40,
                                                background:
                                                    colors.primaryLight,
                                                borderRadius: 10,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent:
                                                    'center',
                                                fontSize: '1.1rem',
                                            }}
                                        >
                                            &#128222;
                                        </div>
                                        <div>
                                            <p
                                                style={{
                                                    fontSize:
                                                        '0.8rem',
                                                    color: '#9ca3af',
                                                    fontWeight: 500,
                                                    margin: 0,
                                                }}
                                            >
                                                Phone
                                            </p>
                                            <a
                                                href={`tel:${phone}`}
                                                style={{
                                                    fontWeight: 600,
                                                    fontSize:
                                                        '0.95rem',
                                                    color: colors.primary,
                                                    textDecoration:
                                                        'none',
                                                }}
                                            >
                                                {phone}
                                            </a>
                                        </div>
                                    </div>
                                )}
                                {email && (
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.75rem',
                                            marginBottom: '1rem',
                                        }}
                                    >
                                        <div
                                            style={{
                                                width: 40,
                                                height: 40,
                                                background:
                                                    colors.primaryLight,
                                                borderRadius: 10,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent:
                                                    'center',
                                                fontSize: '1.1rem',
                                            }}
                                        >
                                            &#9993;
                                        </div>
                                        <div>
                                            <p
                                                style={{
                                                    fontSize:
                                                        '0.8rem',
                                                    color: '#9ca3af',
                                                    fontWeight: 500,
                                                    margin: 0,
                                                }}
                                            >
                                                Email
                                            </p>
                                            <a
                                                href={`mailto:${email}`}
                                                style={{
                                                    fontWeight: 600,
                                                    fontSize:
                                                        '0.95rem',
                                                    color: colors.primary,
                                                    textDecoration:
                                                        'none',
                                                }}
                                            >
                                                {email}
                                            </a>
                                        </div>
                                    </div>
                                )}
                                {address && (
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.75rem',
                                            marginBottom: '1rem',
                                        }}
                                    >
                                        <div
                                            style={{
                                                width: 40,
                                                height: 40,
                                                background:
                                                    colors.primaryLight,
                                                borderRadius: 10,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent:
                                                    'center',
                                                fontSize: '1.1rem',
                                            }}
                                        >
                                            &#128205;
                                        </div>
                                        <div>
                                            <p
                                                style={{
                                                    fontSize:
                                                        '0.8rem',
                                                    color: '#9ca3af',
                                                    fontWeight: 500,
                                                    margin: 0,
                                                }}
                                            >
                                                Address
                                            </p>
                                            <p
                                                style={{
                                                    fontWeight: 600,
                                                    fontSize:
                                                        '0.95rem',
                                                    color: '#111827',
                                                    margin: 0,
                                                    whiteSpace:
                                                        'pre-line',
                                                }}
                                            >
                                                {address}
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                        {/* ── Map ── */}
                        {isLoaded && cordData && (
                            <div
                                style={{
                                    marginTop: '2rem',
                                    borderRadius: 16,
                                    overflow: 'hidden',
                                    border: '1px solid #e5e7eb',
                                    height: 320,
                                }}
                            >
                                <GoogleMap
                                    mapContainerStyle={{
                                        width: '100%',
                                        height: '100%',
                                    }}
                                    center={cordData}
                                    onLoad={onMapLoad}
                                >
                                    {validAddress && (
                                        <Marker position={cordData} />
                                    )}
                                </GoogleMap>
                            </div>
                        )}
                    </div>
                </section>
            )}

            {/* ── CTA Banner ── */}
            <section
                style={{
                    background: colors.gradient,
                    textAlign: 'center',
                    color: colors.primaryDarkText,
                    padding: '5rem 2rem',
                }}
            >
                <h2
                    style={{
                        fontSize: '2.25rem',
                        fontWeight: 900,
                        marginBottom: '0.75rem',
                        color: colors.primaryDarkText,
                    }}
                >
                    Ready to Get Started?
                </h2>
                <p
                    style={{
                        fontSize: '1.1rem',
                        opacity: 0.9,
                        marginBottom: '2rem',
                        maxWidth: 500,
                        marginLeft: 'auto',
                        marginRight: 'auto',
                    }}
                >
                    {`Contact ${businessName} today and let us know how we can help.`}
                </p>
                <div
                    style={{
                        display: 'flex',
                        gap: '1rem',
                        justifyContent: 'center',
                        flexWrap: 'wrap',
                    }}
                >
                    <button
                        onClick={goToBook}
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '0.5rem',
                            background: '#fff',
                            color: colors.primary,
                            padding: '0.9rem 2.25rem',
                            borderRadius: 10,
                            fontWeight: 700,
                            fontSize: '1rem',
                            border: 'none',
                            cursor: 'pointer',
                        }}
                    >
                        &#128197; Book Now
                    </button>
                    {phone && (
                        <a
                            href={`tel:${phone}`}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '0.5rem',
                                background: 'rgba(255,255,255,0.15)',
                                border: '2px solid rgba(255,255,255,0.8)',
                                color: colors.primaryDarkText,
                                padding: '0.9rem 2.25rem',
                                borderRadius: 10,
                                fontWeight: 700,
                                textDecoration: 'none',
                                fontSize: '1rem',
                            }}
                        >
                            &#128222; Call {phone}
                        </a>
                    )}
                    {email && (
                        <a
                            href={`mailto:${email}`}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '0.5rem',
                                background: 'rgba(255,255,255,0.15)',
                                border: '2px solid rgba(255,255,255,0.8)',
                                color: colors.primaryDarkText,
                                padding: '0.9rem 2.25rem',
                                borderRadius: 10,
                                fontWeight: 700,
                                textDecoration: 'none',
                                fontSize: '1rem',
                            }}
                        >
                            &#9993; Email Us
                        </a>
                    )}
                </div>
            </section>

            {/* ── Footer ── */}
            <footer
                style={{
                    background: '#111827',
                    color: '#9ca3af',
                    padding: '2.5rem 2rem',
                }}
            >
                <div
                    style={{
                        maxWidth: 1100,
                        margin: '0 auto',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: '1rem',
                    }}
                >
                    <div>
                        <p
                            style={{
                                fontWeight: 800,
                                color: '#fff',
                                fontSize: '1.1rem',
                                marginBottom: '0.25rem',
                            }}
                        >
                            {businessName}
                        </p>
                    </div>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '1.5rem',
                            fontSize: '0.85rem',
                        }}
                    >
                        {phone && (
                            <a
                                href={`tel:${phone}`}
                                style={{
                                    color: '#9ca3af',
                                    textDecoration: 'none',
                                }}
                            >
                                {phone}
                            </a>
                        )}
                        {email && (
                            <a
                                href={`mailto:${email}`}
                                style={{
                                    color: '#9ca3af',
                                    textDecoration: 'none',
                                }}
                            >
                                {email}
                            </a>
                        )}
                    </div>
                </div>
                <div
                    style={{
                        maxWidth: 1100,
                        margin: '1.5rem auto 0',
                        paddingTop: '1.5rem',
                        borderTop: '1px solid #1f2937',
                        textAlign: 'center',
                    }}
                >
                    <p
                        style={{
                            fontSize: '0.8rem',
                            color: '#6b7280',
                        }}
                    >
                        &copy; {new Date().getFullYear()}{' '}
                        {businessName}. All rights reserved.
                        <span style={{ margin: '0 0.5rem' }}>·</span>
                        Powered by{' '}
                        <a
                            href={poweredByUrl(subdomain)}
                            target='_blank'
                            rel='noopener'
                            style={{ color: '#9ca3af', textDecoration: 'none', fontWeight: 600 }}
                        >
                            {BRAND.name}
                        </a>
                    </p>
                </div>
            </footer>

            {/* ── Responsive styles ── */}
            <style>{`
                @media (max-width: 768px) {
                    .hp2-hero-grid {
                        grid-template-columns: 1fr !important;
                    }
                    .hp2-contact-grid {
                        grid-template-columns: 1fr !important;
                    }
                }
            `}</style>
        </div>
    );
};

export default ModernHomepage;
